/**
 * SiteStep — Step 1: Address + Tariff
 *
 * Partner enters customer address, we geocode it, detect utility territory,
 * and offer tariff selection (from Vortx DB, upload, or skip for AI).
 */

import React, { useState, useCallback } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Divider,
} from '@mui/material';
import { LocationOn, Search } from '@mui/icons-material';
import { useAuth, vortxColors, vortxTypography } from '@vortx/shared-ui';
import { tariffsForLocation } from '../services/partnerService';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function SiteStep({ initialData, onComplete }) {
  const { currentUser } = useAuth();
  const [address, setAddress] = useState(initialData?.address || '');
  const [customerName, setCustomerName] = useState(initialData?.customer_name || '');
  const [lat, setLat] = useState(initialData?.lat || null);
  const [lon, setLon] = useState(initialData?.lon || null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(null);

  const [tariffs, setTariffs] = useState([]);
  const [selectedTariffId, setSelectedTariffId] = useState('');
  const [selectedTariffData, setSelectedTariffData] = useState(initialData?.tariff_data || null);
  const [loadingTariffs, setLoadingTariffs] = useState(false);

  const handleGeocode = useCallback(async () => {
    if (!address.trim()) return;
    setGeocoding(true);
    setGeocodeError(null);

    try {
      // Use the existing geocode endpoint (same one Site DNA uses)
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/api/site_dna/v1/geocode?address=${encodeURIComponent(address)}`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );

      if (!res.ok) {
        // Fallback: let partner manually enter lat/lon or try again
        setGeocodeError('Could not geocode address. Please check and try again.');
        return;
      }

      const data = await res.json();
      const newLat = data.latitude || data.lat;
      const newLon = data.longitude || data.lon;

      if (!newLat || !newLon) {
        setGeocodeError('Geocode returned no coordinates. Please check the address.');
        return;
      }

      setLat(newLat);
      setLon(newLon);

      // Auto-fetch available tariffs for this location
      setLoadingTariffs(true);
      try {
        const availableTariffs = await tariffsForLocation(currentUser, newLat, newLon);
        setTariffs(availableTariffs);
      } catch (e) {
        console.warn('Failed to load tariffs:', e);
        // Non-fatal — partner can skip tariff selection
      } finally {
        setLoadingTariffs(false);
      }
    } catch (e) {
      setGeocodeError(`Geocoding failed: ${e.message}`);
    } finally {
      setGeocoding(false);
    }
  }, [address, currentUser]);

  const handleTariffSelect = useCallback(async (tariffId) => {
    setSelectedTariffId(tariffId);
    if (!tariffId) {
      setSelectedTariffData(null);
      return;
    }

    // For now, store the tariff ID — the auto-propose endpoint will resolve the full data
    // TODO: Fetch full tariff data from Vortx DB
    const tariff = tariffs.find((t) => t.id === tariffId);
    if (tariff) {
      setSelectedTariffData({ _tariff_id: tariff.id, label: tariff.label, utility: tariff.utility });
    }
  }, [tariffs]);

  const handleContinue = useCallback(() => {
    onComplete({
      address,
      lat,
      lon,
      customer_name: customerName,
      tariff_data: selectedTariffData,
    });
  }, [address, lat, lon, customerName, selectedTariffData, onComplete]);

  const canContinue = lat && lon;

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, mb: 3 }}
      >
        Site & Tariff
      </Typography>

      <TextField
        label="Customer Name"
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        placeholder="e.g. Acme Warehouse"
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Site Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          fullWidth
          size="small"
          placeholder="123 Main St, San Francisco, CA"
          onKeyDown={(e) => e.key === 'Enter' && handleGeocode()}
        />
        <Button
          variant="contained"
          onClick={handleGeocode}
          disabled={geocoding || !address.trim()}
          startIcon={geocoding ? <CircularProgress size={16} /> : <Search />}
          sx={{
            bgcolor: vortxColors.gold,
            minWidth: 120,
            '&:hover': { bgcolor: vortxColors.darkGold || '#9a7a40' },
          }}
        >
          Locate
        </Button>
      </Box>

      {geocodeError && <Alert severity="error" sx={{ mb: 2 }}>{geocodeError}</Alert>}

      {lat && lon && (
        <Alert severity="success" icon={<LocationOn />} sx={{ mb: 3 }}>
          Location found: {lat.toFixed(4)}, {lon.toFixed(4)}
        </Alert>
      )}

      {lat && lon && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, mb: 2 }}
          >
            Tariff Selection (Optional)
          </Typography>
          <Typography
            sx={{ fontFamily: vortxTypography.body, fontSize: '0.85rem', opacity: 0.7, mb: 2 }}
          >
            Select a tariff from the Vortx database, or skip and let AI recommend one after you upload the load profile.
          </Typography>

          {loadingTariffs ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '0.85rem' }}>Loading available tariffs...</Typography>
            </Box>
          ) : tariffs.length > 0 ? (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Available Tariffs</InputLabel>
              <Select
                value={selectedTariffId}
                onChange={(e) => handleTariffSelect(e.target.value)}
                label="Available Tariffs"
              >
                <MenuItem value="">
                  <em>Skip — AI will recommend</em>
                </MenuItem>
                {tariffs.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label} ({t.utility}){t.has_demand_charges ? ' — has demand charges' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No tariffs found in Vortx database for this location.
              You can continue without selecting one — AI will attempt to recommend a tariff,
              or you may need to upload a rate schedule.
            </Alert>
          )}
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!canContinue}
          sx={{
            bgcolor: vortxColors.gold,
            fontFamily: vortxTypography.body,
            fontWeight: 600,
            px: 4,
            '&:hover': { bgcolor: vortxColors.darkGold || '#9a7a40' },
          }}
        >
          Continue to Load Profile
        </Button>
      </Box>
    </Box>
  );
}

export default SiteStep;
